'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          action?: string
          callback?: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
        },
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

type TicketOption = {
  key: string
  label: string
  quantityMode: 'fixed' | 'multi-ticket'
  quantity: number
  priceUSD: number
}

type AttendeeRow = {
  name: string
  email: string
  phone: string
}

function toMoney(value: number): string {
  return `$${Math.max(0, Number(value || 0)).toFixed(2)}`
}

function onlyDigits(value: string): string {
  return String(value || '').replace(/\D/g, '')
}

function formatUsPhone(digits: string): string {
  const d = onlyDigits(digits).slice(0, 10)
  if (d.length <= 3) return d
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

function formatIntlPhone(digits: string): string {
  const d = onlyDigits(digits).slice(0, 15)
  return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim()
}

function parseDefaultPhone(phone: string): { country: string; digits: string } {
  const raw = String(phone || '').trim()
  if (!raw) return { country: '+1', digits: '' }
  const normalized = raw.startsWith('+') ? raw : `+${raw}`
  const countryCodes = ['+1', '+44', '+61', '+91', '+52']
  for (const code of countryCodes) {
    if (normalized.startsWith(code)) {
      return {
        country: code,
        digits: onlyDigits(normalized.slice(code.length)),
      }
    }
  }
  return { country: '+1', digits: onlyDigits(normalized) }
}

export default function EventTicketCheckoutForm({
  eventId,
  occurrenceDate,
  ticketOptions,
  preselectedTicketOptionKey,
  defaultReceiptName,
  defaultReceiptEmail,
  defaultReceiptPhone,
  defaultReceiptOrganization,
  defaultReceiptWebsite,
  defaultReceiptIndustry,
  lockReceiptEmail,
  lockReceiptPhone,
  lockReceiptOrganization,
  lockReceiptWebsite,
  lockReceiptIndustry,
  ticketOptionColumnsTablet,
  ticketOptionColumnsDesktop,
  registrationCardWidth,
  ticketCardDensity,
}: {
  eventId: string
  occurrenceDate: string
  ticketOptions: TicketOption[]
  preselectedTicketOptionKey?: string
  defaultReceiptName?: string
  defaultReceiptEmail?: string
  defaultReceiptPhone?: string
  defaultReceiptOrganization?: string
  defaultReceiptWebsite?: string
  defaultReceiptIndustry?: string
  lockReceiptEmail?: boolean
  lockReceiptPhone?: boolean
  lockReceiptOrganization?: boolean
  lockReceiptWebsite?: boolean
  lockReceiptIndustry?: boolean
  ticketOptionColumnsTablet?: number
  ticketOptionColumnsDesktop?: number
  registrationCardWidth?: 'normal' | 'wide'
  ticketCardDensity?: 'comfortable' | 'compact'
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const initialKey =
    ticketOptions.find((o) => o.key === preselectedTicketOptionKey)?.key ||
    ticketOptions[0]?.key ||
    ''

  const parsedDefaultPhone = useMemo(
    () => parseDefaultPhone(String(defaultReceiptPhone || '')),
    [defaultReceiptPhone],
  )

  const [selectedTicketKey, setSelectedTicketKey] = useState<string>(initialKey)
  const [receiptEmail, setReceiptEmail] = useState(String(defaultReceiptEmail || ''))
  const [receiptPhoneCountry, setReceiptPhoneCountry] = useState(parsedDefaultPhone.country)
  const [receiptPhoneDigits, setReceiptPhoneDigits] = useState(parsedDefaultPhone.digits)
  const [receiptOrganization, setReceiptOrganization] = useState(
    String(defaultReceiptOrganization || ''),
  )
  const [receiptWebsite, setReceiptWebsite] = useState(String(defaultReceiptWebsite || ''))
  const [receiptIndustry, setReceiptIndustry] = useState(String(defaultReceiptIndustry || ''))
  const [ccName, setCcName] = useState(String(defaultReceiptName || ''))
  const [promoCode, setPromoCode] = useState('')
  const [showPromoCode, setShowPromoCode] = useState(false)
  const [promoApplying, setPromoApplying] = useState(false)
  const [promoAppliedCode, setPromoAppliedCode] = useState<string | null>(null)
  const [promoQuoteAmountCents, setPromoQuoteAmountCents] = useState<number | null>(null)
  const [promoFeedback, setPromoFeedback] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const [selectedQuantity, setSelectedQuantity] = useState(1)
  const [attendees, setAttendees] = useState<AttendeeRow[]>([])
  const [smsConsentTransactional, setSmsConsentTransactional] = useState(true)
  const [smsConsentMarketing, setSmsConsentMarketing] = useState(true)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [successfulSubmission, setSuccessfulSubmission] = useState(false)
  const [loading, setLoading] = useState(false)
  const [collectLoaded, setCollectLoaded] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaTokenRef = useRef('')
  const [captchaReady, setCaptchaReady] = useState(false)
  const [mounted, setMounted] = useState(false)

  const configuredRef = useRef(false)
  const receiptEmailInputRef = useRef<HTMLInputElement | null>(null)
  const receiptPhoneInputRef = useRef<HTMLInputElement | null>(null)
  const submitPurchaseRef = useRef<(paymentToken: string) => Promise<void>>(async () => {})
  const submitInFlightRef = useRef(false)
  const initializedRef = useRef(false)
  const captchaContainerRef = useRef<HTMLDivElement | null>(null)
  const captchaWidgetIdRef = useRef<string | null>(null)

  const resetCaptcha = useCallback(() => {
    captchaTokenRef.current = ''
    setCaptchaToken('')
    if (captchaWidgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(captchaWidgetIdRef.current)
      } catch {}
    }
  }, [])

  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

  const selectedOption = useMemo(
    () => ticketOptions.find((o) => o.key === selectedTicketKey) || null,
    [selectedTicketKey, ticketOptions],
  )

  useEffect(() => {
    const fromQuery = String(preselectedTicketOptionKey || '').trim()
    if (!fromQuery) return
    const found = ticketOptions.find((o) => o.key === fromQuery)
    if (found && found.key !== selectedTicketKey) {
      setSelectedTicketKey(found.key)
    }
  }, [preselectedTicketOptionKey, selectedTicketKey, ticketOptions])

  const quantityMode = selectedOption?.quantityMode === 'multi-ticket' ? 'multi-ticket' : 'fixed'
  const minQuantity = Math.max(1, Number(selectedOption?.quantity || 1))
  const attendeeQuantity = quantityMode === 'multi-ticket' ? selectedQuantity : minQuantity
  const totalAmount = Math.max(0, Number(selectedOption?.priceUSD || 0)) * attendeeQuantity
  const baseAmountCents = Math.round(totalAmount * 100)
  const effectiveAmountCents = promoQuoteAmountCents ?? baseAmountCents
  const isUSPhone = receiptPhoneCountry === '+1'
  const tabletColumns = Math.max(1, Math.min(3, Number(ticketOptionColumnsTablet || 2)))
  const desktopColumns = Math.max(2, Math.min(4, Number(ticketOptionColumnsDesktop || 3)))
  const tabletGridClass =
    tabletColumns === 1
      ? 'sm:grid-cols-1'
      : tabletColumns === 3
        ? 'sm:grid-cols-3'
        : 'sm:grid-cols-2'
  const desktopGridClass =
    desktopColumns === 2
      ? 'md:grid-cols-2'
      : desktopColumns === 4
        ? 'md:grid-cols-4'
        : 'md:grid-cols-3'
  const registrationWidthClass = registrationCardWidth === 'wide' ? 'max-w-none' : 'max-w-3xl'
  const ticketCardDensityClass =
    ticketCardDensity === 'compact' ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'
  const normalizedPhoneDigits = onlyDigits(receiptPhoneDigits)
  const phoneDisplayValue = isUSPhone
    ? formatUsPhone(normalizedPhoneDigits)
    : formatIntlPhone(normalizedPhoneDigits)
  const receiptPhone = `${receiptPhoneCountry}${normalizedPhoneDigits}`
  const hasProfileSourcedReceiptField =
    !!lockReceiptEmail ||
    !!lockReceiptPhone ||
    !!lockReceiptOrganization ||
    !!lockReceiptWebsite ||
    !!lockReceiptIndustry

  useEffect(() => {
    if (!selectedOption) {
      setSelectedQuantity(1)
      return
    }

    setSelectedQuantity((prev) => {
      if (selectedOption.quantityMode === 'multi-ticket') {
        return Math.max(minQuantity, prev)
      }
      return minQuantity
    })
  }, [selectedOption?.key, selectedOption?.quantityMode, minQuantity, selectedOption])

  useEffect(() => {
    const qty = Math.max(1, attendeeQuantity)
    setAttendees((prev) => {
      const next = Array.from(
        { length: qty },
        (_, idx) => prev[idx] || { name: '', email: '', phone: '' },
      )
      if (qty > 0) {
        next[0] = {
          name: next[0]?.name || String(defaultReceiptName || ''),
          email: next[0]?.email || String(defaultReceiptEmail || ''),
          phone: next[0]?.phone || '',
        }
      }
      return next
    })
  }, [attendeeQuantity, defaultReceiptEmail, defaultReceiptName])

  useEffect(() => {
    setPromoAppliedCode(null)
    setPromoQuoteAmountCents(null)
    setPromoFeedback(null)
  }, [selectedTicketKey, attendeeQuantity])

  const removeAppliedPromoCode = () => {
    setPromoCode('')
    setPromoAppliedCode(null)
    setPromoQuoteAmountCents(null)
    setPromoFeedback(null)
  }

  const applyTicketOptionSelection = (nextKey: string) => {
    setSelectedTicketKey(nextKey)
    if (!pathname) return

    const params = new URLSearchParams(searchParams?.toString() || '')
    const normalizedKey = String(nextKey || '').trim()
    if (!normalizedKey) {
      params.delete('ticketOption')
      params.delete('ticketoption')
      params.delete('option')
    } else {
      params.set('ticketOption', normalizedKey)
      params.delete('ticketoption')
      params.delete('option')
      params.set('scrollTo', 'registration')
    }

    const query = params.toString()
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
  }

  const resetCheckoutForm = (opts?: { restoreInitialSelection?: boolean }) => {
    const shouldRestoreInitial = Boolean(opts?.restoreInitialSelection)
    const targetKey = shouldRestoreInitial ? initialKey : selectedTicketKey
    const targetOption = ticketOptions.find((o) => o.key === targetKey) || ticketOptions[0] || null
    const targetMinQuantity = Math.max(1, Number(targetOption?.quantity || 1))

    if (targetKey && targetKey !== selectedTicketKey) {
      setSelectedTicketKey(targetKey)
    }
    setReceiptEmail(String(defaultReceiptEmail || ''))
    setReceiptPhoneCountry(parsedDefaultPhone.country)
    setReceiptPhoneDigits(parsedDefaultPhone.digits)
    setReceiptOrganization(String(defaultReceiptOrganization || ''))
    setReceiptWebsite(String(defaultReceiptWebsite || ''))
    setReceiptIndustry(String(defaultReceiptIndustry || ''))
    setCcName(String(defaultReceiptName || ''))
    setPromoCode('')
    setShowPromoCode(false)
    setPromoApplying(false)
    setPromoAppliedCode(null)
    setPromoQuoteAmountCents(null)
    setPromoFeedback(null)
    setSmsConsentTransactional(true)
    setSmsConsentMarketing(true)
    setMessage(null)
    setSuccessfulSubmission(false)
    setSelectedQuantity(targetMinQuantity)
    setAttendees(
      Array.from({ length: targetMinQuantity }, (_, idx) =>
        idx === 0
          ? {
              name: String(defaultReceiptName || ''),
              email: String(defaultReceiptEmail || ''),
              phone: '',
            }
          : { name: '', email: '', phone: '' },
      ),
    )
  }

  const applyPromoCode = async () => {
    const code = promoCode.trim().toUpperCase()
    if (!code) {
      setPromoFeedback({ type: 'error', text: 'Enter a promo code first.' })
      setPromoAppliedCode(null)
      setPromoQuoteAmountCents(null)
      return
    }

    setPromoApplying(true)
    setPromoFeedback(null)
    try {
      const response = await fetch('/api/nmi/event-ticket-purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quoteOnly: true,
          eventId,
          occurrenceDate,
          ticketOptionKey: String(selectedOption?.key || '').trim(),
          selectedQuantity: attendeeQuantity,
          receiptEmail: receiptEmail.trim(),
          promoCode: code,
        }),
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result?.success) {
        setPromoFeedback({
          type: 'error',
          text: String(result?.message || 'Promo code is invalid.'),
        })
        setPromoAppliedCode(null)
        setPromoQuoteAmountCents(null)
        return
      }

      const amountCents = Math.max(0, Number(result?.amountCents || 0))
      setPromoAppliedCode(code)
      setPromoQuoteAmountCents(amountCents)
      setPromoFeedback({
        type: 'success',
        text: `Promo code applied. New total ${toMoney(amountCents / 100)}.`,
      })
    } catch (e: any) {
      setPromoFeedback({
        type: 'error',
        text: String(e?.message || 'Unable to validate promo code right now.'),
      })
      setPromoAppliedCode(null)
      setPromoQuoteAmountCents(null)
    } finally {
      setPromoApplying(false)
    }
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || !turnstileSiteKey || typeof window === 'undefined') return
    if (window.turnstile) {
      setCaptchaReady(true)
      return
    }

    const existing = document.querySelector(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]',
    )
    if (existing) {
      existing.addEventListener('load', () => setCaptchaReady(true), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => setCaptchaReady(true)
    script.onerror = () => {
      setMessage({
        type: 'error',
        text: 'Unable to load human verification. Please refresh and try again.',
      })
    }
    document.head.appendChild(script)
  }, [mounted, turnstileSiteKey])

  useEffect(() => {
    if (!mounted || !turnstileSiteKey || !captchaReady || !captchaContainerRef.current) return
    if (typeof window === 'undefined' || !window.turnstile) return
    if (captchaWidgetIdRef.current) return

    captchaWidgetIdRef.current = window.turnstile.render(captchaContainerRef.current, {
      sitekey: turnstileSiteKey,
      action: 'checkout',
      callback: (token: string) => {
        const nextToken = String(token || '')
        captchaTokenRef.current = nextToken
        setCaptchaToken(nextToken)
      },
      'error-callback': () => {
        captchaTokenRef.current = ''
        setCaptchaToken('')
        setMessage({ type: 'error', text: 'Human verification failed. Please try again.' })
      },
      'expired-callback': () => {
        captchaTokenRef.current = ''
        setCaptchaToken('')
      },
      theme: 'dark',
    })
  }, [captchaReady, mounted, turnstileSiteKey])

  useEffect(() => {
    const tokenizationKey = process.env.NEXT_PUBLIC_NMI_TOKENIZATION_KEY
    if (!tokenizationKey) {
      setMessage({
        type: 'error',
        text: 'Payment configuration missing: tokenization key is not set.',
      })
      return
    }

    if (typeof window === 'undefined') return
    if ((window as any).CollectJS) {
      setCollectLoaded(true)
      return
    }

    const existing = document.querySelector('script[src="https://secure.nmi.com/token/Collect.js"]')
    if (existing) {
      if ((window as any).CollectJS) {
        setCollectLoaded(true)
      } else {
        existing.addEventListener('load', () => setCollectLoaded(true), { once: true })
      }
      return
    }

    const script = document.createElement('script')
    script.src = 'https://secure.nmi.com/token/Collect.js'
    script.setAttribute('data-tokenization-key', tokenizationKey)
    script.onload = () => setCollectLoaded(true)
    script.onerror = () => {
      setMessage({
        type: 'error',
        text: 'Unable to load payment form. Please refresh and try again.',
      })
    }
    document.head.appendChild(script)
  }, [])

  const submitPurchase = async (paymentToken: string) => {
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    const ticketOptionKey = String(selectedOption?.key || '').trim()
    if (!ticketOptionKey) {
      setMessage({ type: 'error', text: 'Please choose a ticket option.' })
      submitInFlightRef.current = false
      return
    }

    const emailFromDom = String(receiptEmailInputRef.current?.value || '').trim()
    const syncedReceiptEmail = receiptEmail.trim() || emailFromDom
    const phoneDigitsFromDom = onlyDigits(String(receiptPhoneInputRef.current?.value || ''))
    const syncedPhoneDigits = normalizedPhoneDigits.trim() || phoneDigitsFromDom

    if (syncedReceiptEmail && syncedReceiptEmail !== receiptEmail) {
      setReceiptEmail(syncedReceiptEmail)
    }
    if (syncedPhoneDigits && syncedPhoneDigits !== normalizedPhoneDigits) {
      setReceiptPhoneDigits(syncedPhoneDigits)
    }

    if (!syncedReceiptEmail || !syncedPhoneDigits) {
      setMessage({ type: 'error', text: 'Receipt email and phone are required.' })
      submitInFlightRef.current = false
      return
    }

    if (!ccName.trim()) {
      setMessage({ type: 'error', text: 'Cardholder name is required.' })
      submitInFlightRef.current = false
      return
    }

    if (!smsConsentTransactional) {
      setMessage({
        type: 'error',
        text: 'You must consent to receive non-marketing event text messages to continue.',
      })
      submitInFlightRef.current = false
      return
    }

    for (let i = 0; i < attendees.length; i++) {
      const a = attendees[i]
      if (!String(a?.name || '').trim() || !String(a?.email || '').trim()) {
        setMessage({ type: 'error', text: `Attendee ${i + 1} name and email are required.` })
        submitInFlightRef.current = false
        return
      }
      if (quantityMode === 'multi-ticket' && !String(a?.phone || '').trim()) {
        setMessage({
          type: 'error',
          text: `Attendee ${i + 1} phone is required for multi-ticket options.`,
        })
        submitInFlightRef.current = false
        return
      }
    }

    const resolvedCaptchaToken = String(captchaTokenRef.current || '').trim()

    if (turnstileSiteKey && !resolvedCaptchaToken) {
      setMessage({ type: 'error', text: 'Please complete human verification before checkout.' })
      submitInFlightRef.current = false
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      const confirmationEmail = syncedReceiptEmail
      const resolvedReceiptPhone = `${receiptPhoneCountry}${syncedPhoneDigits}`

      const intentResponse = await fetch('/api/nmi/checkout-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow: 'event-ticket-purchase',
          captchaToken: resolvedCaptchaToken,
          context: {
            eventId,
            occurrenceDate,
            ticketOptionKey,
            selectedQuantity: attendeeQuantity,
            amountCents: effectiveAmountCents,
            receiptEmail: syncedReceiptEmail,
            promoCode: promoCode.trim().toUpperCase(),
          },
        }),
      })
      const intentResult = await intentResponse.json().catch(() => ({}))
      if (!intentResponse.ok || !intentResult?.checkoutIntentToken) {
        resetCaptcha()
        setMessage({
          type: 'error',
          text: String(intentResult?.message || 'Unable to start secure checkout session.'),
        })
        return
      }

      const checkoutIntentToken = String(intentResult.checkoutIntentToken)

      const response = await fetch('/api/nmi/event-ticket-purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          occurrenceDate,
          ticketOptionKey,
          selectedQuantity: attendeeQuantity,
          receiptEmail: syncedReceiptEmail,
          receiptPhone: resolvedReceiptPhone,
          receiptOrganization: receiptOrganization.trim() || undefined,
          receiptWebsite: receiptWebsite.trim() || undefined,
          receiptIndustry: receiptIndustry.trim() || undefined,
          ccName: ccName.trim(),
          smsConsentTransactional,
          smsConsentMarketing,
          promoCode: promoCode.trim() || undefined,
          attendees: attendees.map((a) => ({
            name: a.name.trim(),
            email: a.email.trim(),
            phone: a.phone.trim() || undefined,
          })),
          paymentToken,
          captchaToken: resolvedCaptchaToken,
          checkoutIntentToken,
        }),
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result?.success) {
        resetCaptcha()
        setMessage({ type: 'error', text: String(result?.message || 'Payment failed.') })
        return
      }

      resetCheckoutForm({ restoreInitialSelection: true })
      resetCaptcha()
      setSuccessfulSubmission(true)
      setMessage({
        type: 'success',
        text: `Registration successful! A confirmation email has been sent to ${confirmationEmail || 'your receipt email address'}.`,
      })
    } catch (e: any) {
      resetCaptcha()
      setMessage({ type: 'error', text: String(e?.message || 'Payment request failed.') })
    } finally {
      submitInFlightRef.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    submitPurchaseRef.current = submitPurchase
  }, [submitPurchase])

  useEffect(() => {
    if (!collectLoaded || configuredRef.current) return
    if (typeof window === 'undefined' || !(window as any).CollectJS) return
    try {
      ;(window as any).CollectJS.configure({
        paymentSelector: '#event-ticket-pay-button',
        variant: 'inline',
        fields: {
          ccnumber: {
            selector: '#event-ticket-cc-number',
            title: 'Card Number',
            placeholder: '0000 0000 0000 0000',
          },
          ccexp: {
            selector: '#event-ticket-cc-exp',
            title: 'Card Expiration',
            placeholder: 'MM / YY',
          },
          cvv: {
            display: 'show',
            selector: '#event-ticket-cc-cvv',
            title: 'CVV Code',
            placeholder: 'CVV',
          },
        },
        callback: (response: { token?: string; error?: string }) => {
          if (response?.token) {
            void submitPurchaseRef.current(String(response.token))
            return
          }
          setMessage({
            type: 'error',
            text: String(response?.error || 'Unable to tokenize payment method.'),
          })
        },
      })
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: String(e?.message || 'Unable to initialize payment form.'),
      })
      return
    }

    configuredRef.current = true
  }, [collectLoaded])

  if (!mounted) {
    return <div className="text-sm text-neutral-400">Loading secure checkout...</div>
  }

  if (successfulSubmission && message?.type === 'success') {
    return (
      <div className="space-y-3">
        <div className="mx-auto w-fit text-sm rounded border px-3 py-2 border-green-500/40 text-green-300">
          {message.text}
        </div>
        <button
          type="button"
          onClick={() => {
            resetCheckoutForm({ restoreInitialSelection: true })
          }}
          className="mx-auto block rounded border border-white/20 px-4 py-2 text-sm text-white transition-colors hover:bg-white/5"
        >
          Buy Another Ticket?
        </button>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${registrationWidthClass}`}>
      <div className="space-y-2">
        <div className="block text-sm text-neutral-300">Ticket Option</div>
        <div className={`grid grid-cols-1 gap-2 ${tabletGridClass} ${desktopGridClass}`}>
          {ticketOptions.map((opt) => {
            const selected = opt.key === selectedTicketKey
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => applyTicketOptionSelection(opt.key)}
                className={`text-left rounded border transition-colors ${ticketCardDensityClass} ${
                  selected
                    ? 'border-[#029C90] bg-[#029C90]/10'
                    : 'border-white/10 bg-[#1E1E1E] hover:bg-white/5'
                }`}
              >
                <div className="text-sm font-medium text-white">{opt.label}</div>
                <div className="text-xs text-neutral-400">
                  {opt.quantityMode === 'multi-ticket'
                    ? `Min ${Math.max(1, Number(opt.quantity || 1))} tickets`
                    : `Quantity ${Math.max(1, Number(opt.quantity || 1))}`}{' '}
                  · {toMoney(Math.max(0, Number(opt.priceUSD || 0)))}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex items-center justify-end">
        {!showPromoCode ? (
          <button
            type="button"
            onClick={() => setShowPromoCode(true)}
            className="text-xs text-neutral-400 hover:text-neutral-200 underline underline-offset-2"
          >
            Use Promo Code
          </button>
        ) : null}
      </div>

      {quantityMode === 'multi-ticket' ? (
        <div>
          <label className="block text-sm text-neutral-300 mb-1">Ticket Quantity</label>
          <input
            type="number"
            min={minQuantity}
            step={1}
            value={selectedQuantity}
            onChange={(e) => {
              const next = Math.floor(Number(e.target.value || minQuantity))
              setSelectedQuantity(Math.max(minQuantity, Number.isFinite(next) ? next : minQuantity))
            }}
            className="w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm"
          />
          <div className="mt-1 text-xs text-neutral-400">
            Minimum purchase for this option is {minQuantity} ticket{minQuantity === 1 ? '' : 's'}.
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-neutral-300 mb-1">
            Receipt Owner Email{lockReceiptEmail ? ' *' : ''}
          </label>
          <input
            type="email"
            value={receiptEmail}
            onChange={(e) => setReceiptEmail(e.target.value)}
            ref={receiptEmailInputRef}
            readOnly={!!lockReceiptEmail}
            disabled={!!lockReceiptEmail}
            className={`w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm ${lockReceiptEmail ? 'opacity-70 cursor-not-allowed' : ''}`}
            placeholder="email@example.com"
            suppressHydrationWarning
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-300 mb-1">
            Receipt Owner Phone{lockReceiptPhone ? ' *' : ''}
          </label>
          <div className="flex gap-2">
            <select
              value={receiptPhoneCountry}
              onChange={(e) => setReceiptPhoneCountry(e.target.value)}
              disabled={!!lockReceiptPhone}
              className={`w-28 bg-[#1E1E1E] border border-white/10 rounded px-2 py-2 text-sm ${lockReceiptPhone ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              <option value="+1">US +1</option>
              <option value="+44">UK +44</option>
              <option value="+61">AU +61</option>
              <option value="+91">IN +91</option>
              <option value="+52">MX +52</option>
            </select>
            <input
              type="tel"
              inputMode="tel"
              value={phoneDisplayValue}
              onChange={(e) => setReceiptPhoneDigits(onlyDigits(e.target.value))}
              ref={receiptPhoneInputRef}
              readOnly={!!lockReceiptPhone}
              disabled={!!lockReceiptPhone}
              className={`w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm ${lockReceiptPhone ? 'opacity-70 cursor-not-allowed' : ''}`}
              placeholder={isUSPhone ? '(555) 123-4567' : 'Phone number'}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm text-neutral-300 mb-1">
            Organization (optional){lockReceiptOrganization ? ' *' : ''}
          </label>
          <input
            value={receiptOrganization}
            onChange={(e) => setReceiptOrganization(e.target.value)}
            readOnly={!!lockReceiptOrganization}
            disabled={!!lockReceiptOrganization}
            className={`w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm ${lockReceiptOrganization ? 'opacity-70 cursor-not-allowed' : ''}`}
            placeholder="Your organization"
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-300 mb-1">
            Website (optional){lockReceiptWebsite ? ' *' : ''}
          </label>
          <input
            type="url"
            value={receiptWebsite}
            onChange={(e) => setReceiptWebsite(e.target.value)}
            readOnly={!!lockReceiptWebsite}
            disabled={!!lockReceiptWebsite}
            className={`w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm ${lockReceiptWebsite ? 'opacity-70 cursor-not-allowed' : ''}`}
            placeholder="https://example.com"
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-300 mb-1">
            Industry (optional){lockReceiptIndustry ? ' *' : ''}
          </label>
          <input
            value={receiptIndustry}
            onChange={(e) => setReceiptIndustry(e.target.value)}
            readOnly={!!lockReceiptIndustry}
            disabled={!!lockReceiptIndustry}
            className={`w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm ${lockReceiptIndustry ? 'opacity-70 cursor-not-allowed' : ''}`}
            placeholder="Industry"
          />
        </div>
      </div>

      {hasProfileSourcedReceiptField ? (
        <div className="text-[11px] text-neutral-400">
          * Profile-sourced field and not editable here.
        </div>
      ) : null}

      {showPromoCode ? (
        <div className="space-y-2">
          <label className="block text-sm text-neutral-300 mb-1">Promo Code (optional)</label>
          {promoAppliedCode ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-green-400/40 bg-green-500/10 px-3 py-1 text-xs text-green-200">
              <span>{promoAppliedCode}</span>
              <button
                type="button"
                onClick={removeAppliedPromoCode}
                className="text-green-100 hover:text-white"
                aria-label="Remove promo code"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase())
                  setPromoAppliedCode(null)
                  setPromoQuoteAmountCents(null)
                  setPromoFeedback(null)
                }}
                className="w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm"
                placeholder="PROMO2026"
              />
              <button
                type="button"
                onClick={() => void applyPromoCode()}
                disabled={promoApplying}
                className="rounded border border-white/20 px-3 py-2 text-xs hover:bg-white/5 disabled:opacity-50"
              >
                {promoApplying ? 'Applying...' : 'Submit'}
              </button>
            </div>
          )}
          {promoFeedback ? (
            <div
              className={`text-xs ${promoFeedback.type === 'success' ? 'text-green-300' : 'text-red-300'}`}
            >
              {promoFeedback.text}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="text-sm font-medium">Attendees ({attendees.length})</div>
        {attendees.map((row, idx) => (
          <div
            key={`attendee-${idx}`}
            className={`grid grid-cols-1 gap-2 ${
              quantityMode === 'multi-ticket' ? 'md:grid-cols-3' : 'md:grid-cols-2'
            }`}
          >
            <input
              value={row.name}
              onChange={(e) =>
                setAttendees((prev) =>
                  prev.map((v, i) => (i === idx ? { ...v, name: e.target.value } : v)),
                )
              }
              className="w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm"
              placeholder={`Attendee ${idx + 1} name`}
            />
            <input
              type="email"
              value={row.email}
              onChange={(e) =>
                setAttendees((prev) =>
                  prev.map((v, i) => (i === idx ? { ...v, email: e.target.value } : v)),
                )
              }
              className="w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm"
              placeholder={`Attendee ${idx + 1} email`}
            />
            {quantityMode === 'multi-ticket' ? (
              <input
                type="tel"
                value={row.phone}
                onChange={(e) =>
                  setAttendees((prev) =>
                    prev.map((v, i) => (i === idx ? { ...v, phone: e.target.value } : v)),
                  )
                }
                className="w-full bg-[#1E1E1E] border border-white/10 rounded px-3 py-2 text-sm"
                placeholder={`Attendee ${idx + 1} phone`}
              />
            ) : null}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-sm text-neutral-300 mb-1">CC Name</label>
          <input
            value={ccName}
            onChange={(e) => setCcName(e.target.value)}
            className="h-[56px] w-full bg-[#1E1E1E] border border-white/10 rounded px-2 py-2 text-sm"
            placeholder="Name on card"
            name="cc-name"
            autoComplete="cc-name"
            aria-label="Name on card"
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-300 mb-1">Card Number</label>
          <div
            id="event-ticket-cc-number"
            className="min-h-[40px] bg-[#1E1E1E] border border-white/10 rounded px-2 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-300 mb-1">Expiry</label>
          <div
            id="event-ticket-cc-exp"
            className="min-h-[40px] bg-[#1E1E1E] border border-white/10 rounded px-2 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-300 mb-1">CVV</label>
          <div
            id="event-ticket-cc-cvv"
            className="min-h-[40px] bg-[#1E1E1E] border border-white/10 rounded px-2 py-2"
          />
        </div>
      </div>

      <div className="space-y-2 rounded border border-white/10 p-3">
        <label className="flex items-start gap-2 text-xs text-neutral-300">
          <input
            type="checkbox"
            checked={smsConsentTransactional}
            disabled
            readOnly
            className="mt-0.5 opacity-100 cursor-not-allowed"
          />
          <span>
            By checking this box, I consent to receive non-marketing text messages from DRIVE by
            Suladio about the DRIVE Workshop Event. Message frequency varies, message &amp; data
            rates may apply. Text HELP for assistance, reply STOP to opt out.
          </span>
        </label>
        <label className="flex items-start gap-2 text-xs text-neutral-300">
          <input
            type="checkbox"
            checked={smsConsentMarketing}
            onChange={(e) => setSmsConsentMarketing(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            By checking this box, I consent to receive marketing and promotional messages including
            special offers, discounts, new product updates among others from DRIVE Workshop at the
            phone number provided. Frequency may vary. Message &amp; data rates may apply. Text HELP
            for assistance, reply STOP to opt out.
          </span>
        </label>
      </div>

      {message?.type === 'error' ? (
        <div className="text-sm rounded border px-3 py-2 border-red-500/40 text-red-300">
          {message.text}
        </div>
      ) : null}

      {turnstileSiteKey ? <div ref={captchaContainerRef} /> : null}

      <button
        id="event-ticket-pay-button"
        type="button"
        disabled={loading || !collectLoaded}
        className="mx-auto block rounded border border-[#029C90] px-4 py-2 text-sm text-white transition-colors hover:bg-[#029C90] disabled:opacity-50"
      >
        {loading
          ? 'Processing Payment...'
          : !collectLoaded
            ? 'Loading Secure Payment Form...'
            : `Pay ${toMoney(effectiveAmountCents / 100)}`}
      </button>
    </div>
  )
}
